/**
 * SliceInputSystem - Analog one-thumb movement mapped to normalized track space.
 */

import Phaser from 'phaser';
import { screenToGameStable } from '../utils/responsive';
import { PointerDragTeardown, registerPointerDragHandlers } from './input/pointerBindings';

export interface SliceInputSystemConfig {
  getTrackBounds: () => { left: number; right: number };
  getIsPaused: () => boolean;
  keyboardSensitivity?: number;
}

export class SliceInputSystem {
  private static readonly DEFAULT_KEYBOARD_SENSITIVITY = 1.45;

  private readonly scene: Phaser.Scene;
  private readonly getTrackBounds: () => { left: number; right: number };
  private readonly getIsPaused: () => boolean;
  private readonly keyboardSensitivity: number;

  private isDragging: boolean = false;
  private targetX01: number = 0.5;
  private hasMovedFromCenter: boolean = false;

  private boundPointerDown!: (pointer: Phaser.Input.Pointer) => void;
  private boundPointerMove!: (pointer: Phaser.Input.Pointer) => void;
  private boundPointerUp!: (pointer: Phaser.Input.Pointer) => void;
  private removePointerHandlers: PointerDragTeardown | null = null;
  private leftKey: Phaser.Input.Keyboard.Key | null = null;
  private rightKey: Phaser.Input.Keyboard.Key | null = null;
  private aKey: Phaser.Input.Keyboard.Key | null = null;
  private dKey: Phaser.Input.Keyboard.Key | null = null;

  constructor(scene: Phaser.Scene, config: SliceInputSystemConfig) {
    this.scene = scene;
    this.getTrackBounds = config.getTrackBounds;
    this.getIsPaused = config.getIsPaused;
    this.keyboardSensitivity = config.keyboardSensitivity ?? SliceInputSystem.DEFAULT_KEYBOARD_SENSITIVITY;

    this.setupInput();
  }

  getTargetX01(): number {
    return this.targetX01;
  }

  getHasMovedFromCenter(): boolean {
    return this.hasMovedFromCenter;
  }

  setTargetX01(value: number): void {
    this.targetX01 = Phaser.Math.Clamp(value, 0, 1);
    if (Math.abs(this.targetX01 - 0.5) > 0.08) {
      this.hasMovedFromCenter = true;
    }
  }

  update(deltaMs: number): void {
    if (this.getIsPaused()) return;
    if (deltaMs <= 0) return;

    let keyboardDirection = 0;
    if ((this.leftKey?.isDown ?? false) || (this.aKey?.isDown ?? false)) {
      keyboardDirection -= 1;
    }
    if ((this.rightKey?.isDown ?? false) || (this.dKey?.isDown ?? false)) {
      keyboardDirection += 1;
    }
    if (keyboardDirection === 0) return;

    const deltaX01 = (deltaMs / 1000) * this.keyboardSensitivity * keyboardDirection;
    this.setTargetX01(this.targetX01 + deltaX01);
  }

  projectScreenToX01(screenX: number): number {
    const { x: gameX } = screenToGameStable(screenX, window.innerHeight * 0.5);
    return this.projectGameToX01(gameX);
  }

  private setupInput(): void {
    this.boundPointerDown = this.onPointerDown.bind(this);
    this.boundPointerMove = this.onPointerMove.bind(this);
    this.boundPointerUp = this.onPointerUp.bind(this);

    this.removePointerHandlers = registerPointerDragHandlers(this.scene, {
      onPointerDown: this.boundPointerDown,
      onPointerMove: this.boundPointerMove,
      onPointerUp: this.boundPointerUp,
    });

    const keyboard = this.scene.input.keyboard;
    this.leftKey = keyboard?.addKey(Phaser.Input.Keyboard.KeyCodes.LEFT) ?? null;
    this.rightKey = keyboard?.addKey(Phaser.Input.Keyboard.KeyCodes.RIGHT) ?? null;
    this.aKey = keyboard?.addKey(Phaser.Input.Keyboard.KeyCodes.A) ?? null;
    this.dKey = keyboard?.addKey(Phaser.Input.Keyboard.KeyCodes.D) ?? null;
  }

  private onPointerDown(pointer: Phaser.Input.Pointer): void {
    if (this.getIsPaused()) return;

    this.isDragging = true;
    this.updateTarget(pointer);
  }

  private onPointerMove(pointer: Phaser.Input.Pointer): void {
    if (this.getIsPaused() || !this.isDragging) return;

    this.updateTarget(pointer);
  }

  private onPointerUp(): void {
    this.isDragging = false;
  }

  private updateTarget(pointer: Phaser.Input.Pointer): void {
    const eventX = (pointer.event as PointerEvent | undefined)?.clientX;
    const x01 = typeof eventX === 'number'
      ? this.projectScreenToX01(eventX)
      : this.projectGameToX01(pointer.x);

    this.setTargetX01(x01);
  }

  private projectGameToX01(gameX: number): number {
    const bounds = this.getTrackBounds();
    const width = Math.max(1, bounds.right - bounds.left);
    return Phaser.Math.Clamp((gameX - bounds.left) / width, 0, 1);
  }

  destroy(): void {
    this.removePointerHandlers?.();
    this.removePointerHandlers = null;

    this.isDragging = false;
    this.leftKey = null;
    this.rightKey = null;
    this.aKey = null;
    this.dKey = null;
  }
}
